import offlineDB from "@/components/Utilities/javascripts/localforage";

export const STORAGE_KEY = "offlineAssessment";
export const MAX_ITEMS = 5;

/* =========================
   UTILS
========================= */

function createId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "offline-" + Date.now() + "-" + Math.floor(Math.random() * 100000);
}

function buildName(prefix, first, last) {
  if (!prefix && !first && !last) return null;
  return [prefix, first, last].filter(Boolean).join(" ");
}

function extractPersonName(payload) {
  return (
    payload?.name ||
    payload?.fullname ||
    payload?.fullName ||
    payload?.personName ||
    payload?.patientName ||
    buildName(
      payload?.detail?.assessment?.prefix_name,
      payload?.detail?.assessment?.first_name,
      payload?.detail?.assessment?.last_name
    ) ||
    buildName(
      payload?.detail?.assessment?.c_prefix_name,
      payload?.detail?.assessment?.c_first_name,
      payload?.detail?.assessment?.c_last_name
    ) ||
    "ไม่ระบุชื่อ"
  );
}

async function saveList(list) {
  await offlineDB.ready();

  // 🔥 clone ก่อนเซฟ ป้องกัน proxy หลุด
  const clean = JSON.parse(JSON.stringify(list));

  await offlineDB.setItem(STORAGE_KEY, clean);
}

/* =========================
   GET
========================= */

export async function getOfflineAssessments() {
  try {
    await offlineDB.ready();
    const list = await offlineDB.getItem(STORAGE_KEY);

    if (!Array.isArray(list)) return [];

    // 🔥 บังคับ clone ป้องกัน reference ค้าง
    return JSON.parse(JSON.stringify(list));
  } catch (e) {
    console.error("Error reading offline assessments (IDB):", e);
    return [];
  }
}

/* =========================
   CREATE
========================= */

export async function addOfflineAssessment(payload, type = "null") {
  const list = await getOfflineAssessments();

  const meta = {
    type,
    name: extractPersonName(payload),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    synced: false,
  };

  const item = {
    id: createId(),
    ...meta,
    data: {
      ...meta,
      ...payload,
    },
  };

  list.push(item);

  if (list.length > MAX_ITEMS) {
    list.shift(); // ลบตัวเก่าสุด
  }

  await saveList(list);
  return item;
}

/* =========================
   UPDATE
========================= */

export async function updateOfflineAssessment(
  id,
  newData = {},
  newType = null,
  syncedInfo = false
) {
  const list = await getOfflineAssessments();
  const index = list.findIndex((item) => item.id === id);
  if (index === -1) return null;

  const updatedName = extractPersonName(newData);

  list[index] = {
    ...list[index],
    type: newType ?? list[index].type,
    name: updatedName || list[index].name,
    updated_at: new Date().toISOString(),
    synced: syncedInfo,
    data: {
      ...list[index].data,
      name: updatedName || list[index].name,
      synced: syncedInfo,
      ...newData,
    },
  };

  const cleanList = JSON.parse(JSON.stringify(list));

  await saveList(cleanList);

  return cleanList[index];
}

/* =========================
   DELETE
========================= */

export async function deleteOfflineAssessment(id) {
  const list = await getOfflineAssessments();

  const newList = list.filter(
    (item) => String(item.id) !== String(id)
  );

  if (newList.length === list.length) {
    console.log("ไม่เจอ id ที่จะลบ");
    return { success: false, list: null };
  }

  await saveList(newList);

  return { success: true, list: newList };
}

/* =========================
   CLEAR
========================= */

export async function clearOfflineAssessments() {
  await offlineDB.ready();
  await offlineDB.removeItem(STORAGE_KEY);
}
